import { FC } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import { useUploadToUploadCare } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import { dayjs } from 'utils/providers/Timezone';

import { TASK_FORM, TASK_SCHEMA } from '../forms/schema';
import TaskForm from '../forms/TaskForm';
import { useCreateTask } from '../hooks';

interface PROPS {
  params: Record<string, any>;
}

const CreateTaskModal: FC<PROPS> = ({ params }) => {
  const { hideModal } = useModal();
  const { mutateAsync: uploadFiles, isLoading: uploadLoading } =
    useUploadToUploadCare();
  const { mutate: createTask, isLoading: createLoading } =
    useCreateTask(params);

  const FORM_INIT = {
    title: '',
    dueAt: dayjs().toDate(),
    priority: 'LOW',
    leadId: params.leadId || '',
    content: '',
    accountUserId: [],
    type: 'TODO',
    attachments: [],
    tagId: [],
  };

  /**
   * Submits the form to create the deal
   * @param {typeof FORM_INIT} value
   * @param {import('formik').FormikHelpers} formikHelpers
   * @return {void | Promise<any>}
   */
  const onSubmit = async (
    { attachments, ...rest }: TASK_FORM,
    { setSubmitting }: FormikHelpers<TASK_FORM>,
  ) => {
    const uploadedAttachments =
      attachments?.length && (await uploadFiles(attachments));

    createTask(
      {
        ...(uploadedAttachments && {
          attachments: uploadedAttachments.files.map((file) => ({
            fileUrl: file.cdnUrl,
            fileType: file.mimeType,
            fileName: file.originalFilename,
            fileSize: file.size,
            source: 'Task',
            ...(file.isImage && { fileThumbnail: file.cdnUrl }),
          })),
        }),
        ...params,
        ...rest,
      },
      {
        onSuccess: hideModal,
        onSettled: () => setSubmitting(false),
      },
    );
  };

  return (
    <Formik<TASK_FORM>
      enableReinitialize
      initialValues={FORM_INIT}
      onSubmit={onSubmit}
      validationSchema={TASK_SCHEMA}
    >
      {() => <TaskForm isLoading={uploadLoading || createLoading} />}
    </Formik>
  );
};

export default CreateTaskModal;
